import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import axios from 'axios';
import { API_BASE } from '../api';
import { getLogoPath } from '../teamUtils';
import { logEvent } from '../apiUtils';
import '../App.css';

const TeamPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.classList.add('team-page-bg');
    logEvent('visited_team');
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/sports/team/${teamId}`);
        setData(res.data);
      } catch (e) {
        if (e.response?.status === 404) setError('Team not found.');
        else setError('Failed to load team data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => document.body.classList.remove('team-page-bg');
  }, [teamId]);

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    // dateStr is YYYY-MM-DD; add noon time to avoid timezone-shift issues
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderEventRow = (evt, idx) => {
    const homeScore = evt.intHomeScore;
    const awayScore = evt.intAwayScore;
    const hasScore = homeScore !== null && homeScore !== '' && awayScore !== null && awayScore !== '';

    return (
      <div
        key={evt.idEvent || idx}
        className="team-event-row clickable"
        onClick={() => navigate(`/game/${evt.idEvent}`)}
      >
        <span className="tev-date">{formatEventDate(evt.dateEvent)}</span>
        <span className="tev-teams">
          <span className="tev-home">{evt.strHomeTeam}</span>
          <span className="tev-vs">vs</span>
          <span className="tev-away">{evt.strAwayTeam}</span>
        </span>
        {hasScore ? (
          <span className="tev-score">{homeScore} – {awayScore}</span>
        ) : (
          evt.strTime && <span className="tev-time">{evt.strTime}</span>
        )}
        <span className={`tev-status ${evt.strStatus === 'NS' ? 'tev-ns' : evt.strStatus ? 'tev-ft' : ''}`}>
          {evt.strStatus === 'NS' ? 'Upcoming' : evt.strStatus || ''}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <BaseLayout>
        <div className="entity-page-container">
          <FlameLoader text="Loading team..." />
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout>
        <div className="entity-page-container">
          <p className="error-text">{error}</p>
        </div>
      </BaseLayout>
    );
  }

  if (!data?.team) return null;

  const { team, next_events, last_events, top_pickers } = data;

  return (
    <BaseLayout>
      <div className="entity-page-container">

        {/* ── Team Header ── */}
        <div className="entity-header">
          <img
            src={getLogoPath(team.strTeam)}
            alt={team.strTeam}
            className="entity-logo"
            onError={(e) => {
              if (!e.target.dataset.fallback && team.strTeamBadge) {
                e.target.dataset.fallback = 'true';
                e.target.src = `${team.strTeamBadge}/medium`;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
          <div className="entity-header-info">
            <h1 className="entity-page-name">{team.strTeam}</h1>
            <div className="entity-meta-row">
              {team.idLeague && (
                <Link to={`/league/${team.idLeague}`} className="entity-league-badge">
                  {team.strLeague}
                </Link>
              )}
              {team.strCountry && <span className="entity-meta-tag">{team.strCountry}</span>}
              {team.strSport && <span className="entity-meta-tag">{team.strSport}</span>}
            </div>
            <div className="entity-stat-pills">
              {team.intFormedYear && (
                <span className="entity-stat-pill">
                  <span className="pill-label">Founded</span>
                  {team.intFormedYear}
                </span>
              )}
              {team.strStadium && (
                <span className="entity-stat-pill">
                  <span className="pill-label">Stadium</span>
                  {team.strStadium}
                </span>
              )}
              {team.intStadiumCapacity && (
                <span className="entity-stat-pill">
                  <span className="pill-label">Capacity</span>
                  {parseInt(team.intStadiumCapacity).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>

        {team.strDescriptionEN && (
          <p className="entity-description">
            {team.strDescriptionEN.length > 500
              ? team.strDescriptionEN.slice(0, 500) + '…'
              : team.strDescriptionEN}
          </p>
        )}

        {/* ── Recent Games ── */}
        {last_events?.length > 0 && (
          <section className="entity-section">
            <h2 className="section-title">Recent Games</h2>
            <div className="team-events-list">
              {last_events.map(renderEventRow)}
            </div>
          </section>
        )}

        {/* ── Upcoming Games ── */}
        {next_events?.length > 0 && (
          <section className="entity-section">
            <h2 className="section-title">Upcoming Games</h2>
            <div className="team-events-list">
              {next_events.map(renderEventRow)}
            </div>
          </section>
        )}

        {/* ── Top FlamePicks Pickers ── */}
        <section className="entity-section">
          <h2 className="section-title">Most Loyal Pickers</h2>
          {top_pickers?.length > 0 ? (
            <div className="pickers-table-wrap">
              <table className="pickers-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Picks</th>
                    <th>W</th>
                    <th>L</th>
                    <th>Win%</th>
                  </tr>
                </thead>
                <tbody>
                  {top_pickers.map((p, i) => (
                    <tr
                      key={p.user_id}
                      className="picker-row clickable"
                      onClick={() => navigate(`/user/${p.username}`)}
                    >
                      <td className="rank-col">{i + 1}</td>
                      <td className="username-col">{p.username}</td>
                      <td>{p.total_picks}</td>
                      <td className="win-text">{p.wins}</td>
                      <td className="loss-text">{p.losses}</td>
                      <td>{p.win_pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data-text">No FlamePicks data for this team yet.</p>
          )}
        </section>

      </div>
    </BaseLayout>
  );
};

export default TeamPage;
